import React, { useEffect, useState } from "react";
import api, { formatApiErrorDetail } from "@/lib/api";
import { toast } from "sonner";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription,
} from "@/components/ui/dialog";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { Plus, Pencil, Trash2, Sparkles, Video, ChevronUp, ChevronDown, X, Image as ImageIcon } from "lucide-react";
import FileUploader from "@/components/FileUploader";
import RichTextEditor from "@/components/RichTextEditor";
import { useAuth } from "@/context/AuthContext";

const A_MAX_PHOTOS = 10;

const inputCls =
  "w-full bg-white border border-[#e5e0d8] text-[#1c1a17] rounded-sm px-3 py-2.5 text-sm focus:outline-none focus:ring-1 focus:ring-[#234333] focus:border-[#234333]";

const empty = {
  id: "",
  title: "",
  amenity_type: "",
  description: "",
  video_link: "",
  order: 0,
  status: "Active",
  photos: [],
  booking_enabled: false,
  half_day_charge: 0,
  full_day_charge: 0,
  security_deposit: 0,
  tax_percent: 0,
  time_slots: [],
  seating_options: [],
  av_options: [],
  catering_options: [],
  additional_facility_options: [],
  terms_and_conditions: "",
  cancellation_policy: "",
  usage_guidelines: "",
  privacy_policy: "",
};

function Field({ label, children, full }) {
  return (
    <label className={`block ${full ? "md:col-span-2" : ""}`}>
      <div className="text-[10px] tracking-[0.2em] uppercase text-[#6b655b] font-semibold mb-2">{label}</div>
      {children}
    </label>
  );
}

export default function Amenities() {
  const { role } = useAuth();
  const canEdit = ["super_admin", "editor", "infrastructure"].includes(role);
  const [items, setItems] = useState([]);
  const [types, setTypes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState(empty);
  const [saving, setSaving] = useState(false);
  const [confirmDelete, setConfirmDelete] = useState(null);
  const [q, setQ] = useState("");

  const fetchAll = async () => {
    setLoading(true);
    try {
      const [amenRes, typesRes] = await Promise.all([
        api.get("/admin/amenities"),
        api.get("/masters/amenity_type"),
      ]);
      setItems(amenRes.data);
      setTypes(typesRes.data.map((t) => t.name).filter(Boolean));
    } catch (err) {
      toast.error(formatApiErrorDetail(err?.response?.data?.detail) || "Failed to load");
    } finally { setLoading(false); }
  };
  useEffect(() => { fetchAll(); }, []);

  const filtered = q.trim()
    ? items.filter((i) => [i.title, i.amenity_type].some((x) => (x || "").toLowerCase().includes(q.trim().toLowerCase())))
    : items;

  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));
  const openAdd = () => {
    if (!types.length) {
      toast.error("Please add Amenity Types under Masters → Amenity Type first.");
      return;
    }
    setForm({ ...empty, order: items.length, amenity_type: types[0] || "" });
    setOpen(true);
  };
  const openEdit = (p) => {
    setForm({
      id: p.id,
      title: p.title || "",
      amenity_type: p.amenity_type || (types[0] || ""),
      description: p.description || "",
      video_link: p.video_link || "",
      order: p.order || 0,
      status: p.status || "Active",
      photos: Array.isArray(p.photos) ? p.photos.map((ph, i) => ({
        url: ph.url || "", public_id: ph.public_id || "", order: i,
      })) : [],
      booking_enabled: !!p.booking_enabled,
      half_day_charge: Number(p.half_day_charge) || 0,
      full_day_charge: Number(p.full_day_charge) || 0,
      security_deposit: Number(p.security_deposit) || 0,
      tax_percent: Number(p.tax_percent) || 0,
      time_slots: Array.isArray(p.time_slots) ? p.time_slots : [],
      seating_options: Array.isArray(p.seating_options) ? p.seating_options : [],
      av_options: Array.isArray(p.av_options) ? p.av_options : [],
      catering_options: Array.isArray(p.catering_options) ? p.catering_options.map((o) => ({ name: o.name || "", price: Number(o.price) || 0 })) : [],
      additional_facility_options: Array.isArray(p.additional_facility_options) ? p.additional_facility_options.map((o) => ({ name: o.name || "", price: Number(o.price) || 0 })) : [],
      terms_and_conditions: p.terms_and_conditions || "",
      cancellation_policy: p.cancellation_policy || "",
      usage_guidelines: p.usage_guidelines || "",
      privacy_policy: p.privacy_policy || "",
    });
    setOpen(true);
  };

  const save = async () => {
    if (!form.title.trim()) return toast.error("Title is required");
    const link = (form.video_link || "").trim();
    if (link && !/^https?:\/\//i.test(link)) return toast.error("Video URL must start with http:// or https://");
    const cleaned = (form.photos || [])
      .filter((p) => (p.url || "").trim())
      .slice(0, A_MAX_PHOTOS)
      .map((p, i) => ({ url: p.url, public_id: p.public_id || "", order: i }));
    const payload = {
      title: form.title.trim(),
      amenity_type: form.amenity_type || "",
      description: form.description || "",
      video_link: link,
      order: Number(form.order) || 0,
      status: form.status === "Inactive" ? "Inactive" : "Active",
      photos: cleaned,
      booking_enabled: !!form.booking_enabled,
      half_day_charge: Number(form.half_day_charge) || 0,
      full_day_charge: Number(form.full_day_charge) || 0,
      security_deposit: Number(form.security_deposit) || 0,
      tax_percent: Number(form.tax_percent) || 0,
      time_slots: [],
      seating_options: (form.seating_options || []).map((s) => (s || "").trim()).filter(Boolean),
      av_options: (form.av_options || []).map((s) => (s || "").trim()).filter(Boolean),
      catering_options: [],
      additional_facility_options: [],
      terms_and_conditions: form.terms_and_conditions || "",
      cancellation_policy: form.cancellation_policy || "",
      usage_guidelines: form.usage_guidelines || "",
      privacy_policy: form.privacy_policy || "",
    };
    setSaving(true);
    try {
      if (form.id) {
        await api.put(`/admin/amenities/${form.id}`, payload);
        toast.success("Amenity updated");
      } else {
        await api.post("/admin/amenities", payload);
        toast.success("Amenity added");
      }
      setOpen(false); fetchAll();
    } catch (err) {
      toast.error(formatApiErrorDetail(err?.response?.data?.detail) || "Save failed");
    } finally { setSaving(false); }
  };

  const remove = async () => {
    if (!confirmDelete) return;
    try {
      await api.delete(`/admin/amenities/${confirmDelete.id}`);
      toast.success("Amenity deleted");
      setConfirmDelete(null);
      fetchAll();
    } catch (err) {
      toast.error(formatApiErrorDetail(err?.response?.data?.detail) || "Delete failed");
    }
  };

  return (
    <div className="space-y-6" data-testid="admin-amenities">
      <div className="flex items-center justify-between gap-3 flex-wrap">
        <div>
          <div className="text-[10px] tracking-[0.25em] uppercase text-[#6b655b] font-semibold">Infrastructure</div>
          <h1 className="font-serif text-2xl text-[#1c1a17]">Amenities</h1>
          <div className="text-sm text-[#6b655b] mt-0.5">Facilities and amenities provided by GS Mandal — surfaced on the public Trust site.</div>
        </div>
        {canEdit && (
          <button type="button" onClick={openAdd} data-testid="amen-add-btn" className="inline-flex items-center gap-2 bg-[#234333] hover:bg-[#1a3326] text-white px-4 py-2.5 text-sm font-semibold rounded-sm">
            <Plus size={16} /> Add Amenity
          </button>
        )}
      </div>

      <div className="flex flex-wrap gap-3 items-center bg-white border border-[#e5e0d8] rounded-sm p-3">
        <input data-testid="amen-search" className={`${inputCls} flex-1 min-w-[220px]`} placeholder="Search title or amenity type…" value={q} onChange={(e) => setQ(e.target.value)} />
      </div>

      {loading ? (
        <div className="text-sm text-[#6b655b]">Loading…</div>
      ) : filtered.length === 0 ? (
        <div className="bg-white rounded-sm border border-[#e5e0d8] p-10 text-center">
          <Sparkles size={36} className="mx-auto text-[#bcb6aa]" strokeWidth={1.2} />
          <div className="mt-3 text-sm text-[#6b655b]">No amenities yet. Add your first one.</div>
        </div>
      ) : (
        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4">
          {filtered.map((p) => (
            <div key={p.id} data-testid={`amen-card-${p.id}`} className="bg-white border border-[#e5e0d8] rounded-sm overflow-hidden flex flex-col">
              <div className="aspect-video bg-[#f5f1e8] overflow-hidden relative">
                {(p.photos && p.photos[0] && p.photos[0].url) ? (
                  <img src={p.photos[0].url} alt={p.title} className="w-full h-full object-cover" />
                ) : (
                  <div className="w-full h-full flex items-center justify-center text-[#bcb6aa]"><Sparkles size={32} /></div>
                )}
                {(p.photos || []).length > 1 && (
                  <div className="absolute bottom-2 right-2 bg-black/70 text-white text-[10px] px-1.5 py-0.5 rounded-sm font-semibold">
                    +{(p.photos || []).length - 1} photos
                  </div>
                )}
                {p.video_link && (
                  <div className="absolute top-2 right-2 bg-red-600 text-white text-[10px] uppercase tracking-wider px-1.5 py-0.5 rounded-sm font-bold inline-flex items-center gap-1"><Video size={10} />Video</div>
                )}
                {p.status !== "Active" && <div className="absolute top-2 left-2 bg-red-100 text-red-700 text-[10px] uppercase tracking-wider px-1.5 py-0.5 rounded-sm font-semibold">Inactive</div>}
              </div>
              <div className="p-3 flex-1 flex flex-col">
                <div className="text-[10px] tracking-[0.2em] uppercase text-[#234333] font-semibold">{p.amenity_type}</div>
                <div className="font-semibold text-[#1c1a17] leading-snug text-sm mt-0.5">{p.title}</div>
                {p.description && <div className="mt-2 text-xs text-[#5b5650] line-clamp-3">{p.description.replace(/<[^>]*>/g, "")}</div>}
                {canEdit && (
                  <div className="mt-auto pt-3 flex items-center gap-1.5">
                    <button type="button" onClick={() => openEdit(p)} data-testid={`amen-edit-${p.id}`} className="inline-flex items-center gap-1 text-[#234333] hover:bg-[#234333]/10 text-[11px] font-semibold px-2 py-1 rounded-sm"><Pencil size={11} />Edit</button>
                    <button type="button" onClick={() => setConfirmDelete(p)} data-testid={`amen-delete-${p.id}`} className="inline-flex items-center gap-1 text-red-700 hover:bg-red-50 text-[11px] font-semibold px-2 py-1 rounded-sm"><Trash2 size={11} />Delete</button>
                  </div>
                )}
              </div>
            </div>
          ))}
        </div>
      )}

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="bg-[#fbf8f1] max-w-3xl max-h-[92vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="font-serif text-2xl">{form.id ? "Edit Amenity" : "Add Amenity"}</DialogTitle>
            <DialogDescription>Facilities and amenities that GS Mandal offers — library, hostel, sports, transport and more.</DialogDescription>
          </DialogHeader>
          <div className="grid md:grid-cols-2 gap-4">
            <Field label="Title *">
              <input data-testid="amen-input-title" type="text" maxLength={200} className={inputCls} value={form.title} onChange={(e) => set("title", e.target.value)} />
            </Field>
            <Field label="Type of Amenity *">
              <select data-testid="amen-input-type" className={inputCls} value={form.amenity_type} onChange={(e) => set("amenity_type", e.target.value)}>
                {types.length === 0 && <option value="">— No types configured —</option>}
                {types.map((t) => <option key={t} value={t}>{t}</option>)}
              </select>
              {types.length === 0 && (
                <div className="mt-1.5 text-[11px] text-red-700" data-testid="amen-type-empty">
                  Add Amenity Types under Masters → Amenity Type first.
                </div>
              )}
            </Field>
            <Field label="Description" full>
              <div data-testid="amen-input-description">
                <RichTextEditor
                  value={form.description || ""}
                  onChange={(html) => set("description", html || "")}
                  placeholder="Describe the amenity — capacity, hours, who it serves, notable features…"
                  minHeight={220}
                />
              </div>
              <div className="mt-1 text-[10px] text-[#8a8478]">Rich text · formatting, links, alignment supported</div>
            </Field>
            <Field label={`Photos (up to ${A_MAX_PHOTOS}, max 200 KB each)`} full>
              <PhotosManager photos={form.photos} onChange={(next) => set("photos", next)} />
            </Field>
            <Field label="Video URL (optional)" full>
              <input data-testid="amen-input-video" type="url" maxLength={800} className={inputCls} value={form.video_link} onChange={(e) => set("video_link", e.target.value)} placeholder="e.g. https://youtu.be/…" />
            </Field>
            <Field label="Display Order">
              <input data-testid="amen-input-order" type="number" min="0" className={inputCls} value={form.order} onChange={(e) => set("order", e.target.value)} />
            </Field>
            <Field label="Status">
              <select data-testid="amen-input-status" className={inputCls} value={form.status} onChange={(e) => set("status", e.target.value)}>
                <option value="Active">Active</option>
                <option value="Inactive">Inactive</option>
              </select>
            </Field>
          </div>

          {/* ---- Online booking configuration ---- */}
          <div className="mt-6 border-t border-[#e5e0d8] pt-5">
            <label className="inline-flex items-center gap-2 cursor-pointer select-none" data-testid="amen-toggle-booking">
              <input type="checkbox" className="h-4 w-4 accent-[#234333]" checked={!!form.booking_enabled} onChange={(e) => set("booking_enabled", e.target.checked)} />
              <span className="text-sm font-semibold text-[#1c1a17]">Enable online booking for this amenity</span>
            </label>
            <div className="mt-1 text-[11px] text-[#6b655b]">
              Users will see a &ldquo;Check Availability &amp; Book&rdquo; button on the public Amenities page and pay through CCAvenue or NEFT.
            </div>
          </div>

          {form.booking_enabled && (
            <div className="mt-4 bg-[#faf8f2] border border-[#e5e0d8] rounded-sm p-4 space-y-5" data-testid="amen-booking-config">
              <div className="grid md:grid-cols-4 gap-4">
                <Field label="Half-day Charge (₹)">
                  <input data-testid="amen-input-half-charge" type="number" min="0" step="1" className={inputCls} value={form.half_day_charge} onChange={(e) => set("half_day_charge", e.target.value)} />
                </Field>
                <Field label="Full-day Charge (₹)">
                  <input data-testid="amen-input-full-charge" type="number" min="0" step="1" className={inputCls} value={form.full_day_charge} onChange={(e) => set("full_day_charge", e.target.value)} />
                </Field>
                <Field label="Security Deposit (₹)">
                  <input data-testid="amen-input-deposit" type="number" min="0" step="1" className={inputCls} value={form.security_deposit} onChange={(e) => set("security_deposit", e.target.value)} />
                </Field>
                <Field label="Tax (%)">
                  <input data-testid="amen-input-tax" type="number" min="0" max="100" step="0.5" className={inputCls} value={form.tax_percent} onChange={(e) => set("tax_percent", e.target.value)} />
                </Field>
              </div>

              <div className="rounded-sm bg-white border border-[#e5e0d8] p-3 text-xs text-[#5b5650]" data-testid="amen-duration-hint">
                <b className="text-[#234333]">Duration rules:</b> Half day slots are fixed at <b>4 hours</b>, full day slots at <b>8 hours</b>. The event end time on each booking is auto-calculated from the start time.
              </div>

              <StringListEditor
                label="Seating Arrangement Options (included in cost)"
                hint="e.g. Theatre style, Classroom, U-shape, Banquet, Boardroom"
                testidPrefix="amen-seating"
                list={form.seating_options}
                onChange={(next) => set("seating_options", next)}
              />
              <StringListEditor
                label="Audio-Visual Equipment Options (included in cost)"
                hint="e.g. Projector, Wireless mic, Podium, Laptop, Whiteboard"
                testidPrefix="amen-av"
                list={form.av_options}
                onChange={(next) => set("av_options", next)}
              />

              <div className="grid gap-4">
                <Field label="Booking Terms &amp; Conditions" full>
                  <div data-testid="amen-input-terms">
                    <RichTextEditor
                      value={form.terms_and_conditions || ""}
                      onChange={(html) => set("terms_and_conditions", html || "")}
                      placeholder="What the applicant agrees to at the time of booking…"
                      minHeight={140}
                    />
                  </div>
                </Field>
                <Field label="Cancellation Policy" full>
                  <div data-testid="amen-input-cancellation">
                    <RichTextEditor
                      value={form.cancellation_policy || ""}
                      onChange={(html) => set("cancellation_policy", html || "")}
                      placeholder="Refund window, cancellation charges, deposit forfeiture…"
                      minHeight={140}
                    />
                  </div>
                </Field>
                <Field label="Usage Guidelines" full>
                  <div data-testid="amen-input-guidelines">
                    <RichTextEditor
                      value={form.usage_guidelines || ""}
                      onChange={(html) => set("usage_guidelines", html || "")}
                      placeholder="Do&rsquo;s and don&rsquo;ts inside the premises…"
                      minHeight={140}
                    />
                  </div>
                </Field>
                <Field label="Privacy Policy" full>
                  <div data-testid="amen-input-privacy">
                    <RichTextEditor
                      value={form.privacy_policy || ""}
                      onChange={(html) => set("privacy_policy", html || "")}
                      placeholder="How applicant details, ID proofs and payment data are collected, used and protected…"
                      minHeight={140}
                    />
                  </div>
                </Field>
              </div>
            </div>
          )}

          <DialogFooter>
            <button type="button" onClick={() => setOpen(false)} className="text-sm px-4 py-2.5 rounded-sm border border-[#e5e0d8] text-[#6b655b] hover:bg-[#f5f1e8]">Cancel</button>
            <button type="button" onClick={save} disabled={saving} data-testid="amen-save-btn" className="text-sm bg-[#234333] hover:bg-[#1a3326] text-white px-4 py-2.5 rounded-sm font-semibold disabled:opacity-60">
              {saving ? "Saving…" : (form.id ? "Update" : "Add")}
            </button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <AlertDialog open={!!confirmDelete} onOpenChange={(o) => !o && setConfirmDelete(null)}>
        <AlertDialogContent className="bg-[#fbf8f1]">
          <AlertDialogHeader>
            <AlertDialogTitle className="font-serif text-2xl">Delete amenity?</AlertDialogTitle>
            <AlertDialogDescription>&ldquo;{confirmDelete?.title}&rdquo; and all its photos will be permanently removed.</AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={remove} data-testid="amen-confirm-delete" className="bg-red-700 hover:bg-red-800 text-white">Delete</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}

function StringListEditor({ label, hint, testidPrefix, list, onChange }) {
  const arr = Array.isArray(list) ? list : [];
  const add = () => onChange([...arr, ""]);
  const setAt = (i, v) => onChange(arr.map((x, idx) => (idx === i ? v : x)));
  const remove = (i) => onChange(arr.filter((_, idx) => idx !== i));
  return (
    <div data-testid={`${testidPrefix}-list`}>
      <div className="text-[10px] tracking-[0.2em] uppercase text-[#6b655b] font-semibold mb-2">{label}</div>
      {hint && <div className="text-[11px] text-[#8a8478] -mt-1 mb-2">{hint}</div>}
      <div className="space-y-2">
        {arr.map((v, i) => (
          <div key={i} className="flex items-center gap-2">
            <input
              data-testid={`${testidPrefix}-input-${i}`}
              className={inputCls}
              value={v}
              onChange={(e) => setAt(i, e.target.value)}
              maxLength={120}
              placeholder="Option name"
            />
            <button type="button" onClick={() => remove(i)} data-testid={`${testidPrefix}-remove-${i}`} className="text-red-700 hover:bg-red-50 p-2 rounded-sm" aria-label="Remove"><X size={14} /></button>
          </div>
        ))}
        <button type="button" onClick={add} data-testid={`${testidPrefix}-add`} className="inline-flex items-center gap-1.5 text-[#234333] hover:bg-[#234333]/10 text-xs font-semibold px-3 py-1.5 rounded-sm border border-[#e5e0d8]">
          <Plus size={12} /> Add option
        </button>
      </div>
    </div>
  );
}

function PricedOptionsEditor({ label, hint, testidPrefix, list, onChange }) {
  const arr = Array.isArray(list) ? list : [];
  const add = () => onChange([...arr, { name: "", price: 0 }]);
  const setAt = (i, patch) => onChange(arr.map((x, idx) => (idx === i ? { ...x, ...patch } : x)));
  const remove = (i) => onChange(arr.filter((_, idx) => idx !== i));
  return (
    <div data-testid={`${testidPrefix}-list`}>
      <div className="text-[10px] tracking-[0.2em] uppercase text-[#6b655b] font-semibold mb-2">{label}</div>
      {hint && <div className="text-[11px] text-[#8a8478] -mt-1 mb-2">{hint}</div>}
      <div className="space-y-2">
        {arr.map((o, i) => (
          <div key={i} className="grid grid-cols-[1fr_120px_auto] gap-2 items-center">
            <input
              data-testid={`${testidPrefix}-name-${i}`}
              className={inputCls}
              value={o.name || ""}
              onChange={(e) => setAt(i, { name: e.target.value })}
              maxLength={120}
              placeholder="Option name"
            />
            <input
              data-testid={`${testidPrefix}-price-${i}`}
              type="number"
              min="0"
              step="1"
              className={inputCls}
              value={o.price ?? 0}
              onChange={(e) => setAt(i, { price: Number(e.target.value) || 0 })}
              placeholder="₹"
            />
            <button type="button" onClick={() => remove(i)} data-testid={`${testidPrefix}-remove-${i}`} className="text-red-700 hover:bg-red-50 p-2 rounded-sm" aria-label="Remove"><X size={14} /></button>
          </div>
        ))}
        <button type="button" onClick={add} data-testid={`${testidPrefix}-add`} className="inline-flex items-center gap-1.5 text-[#234333] hover:bg-[#234333]/10 text-xs font-semibold px-3 py-1.5 rounded-sm border border-[#e5e0d8]">
          <Plus size={12} /> Add option
        </button>
      </div>
    </div>
  );
}

function PhotosManager({ photos, onChange }) {
  const list = photos || [];
  const setAt = (idx, patch) => onChange(list.map((p, i) => (i === idx ? { ...p, ...patch } : p)));
  const remove = (idx) => onChange(list.filter((_, i) => i !== idx));
  const move = (idx, dir) => {
    const j = idx + dir;
    if (j < 0 || j >= list.length) return;
    const next = list.slice();
    [next[idx], next[j]] = [next[j], next[idx]];
    onChange(next);
  };
  const addSlot = () => {
    if (list.length >= A_MAX_PHOTOS) return;
    onChange([...list, { url: "", public_id: "", order: list.length }]);
  };

  return (
    <div data-testid="amen-photos">
      <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-3">
        {list.map((p, i) => (
          <div key={i} data-testid={`amen-photo-slot-${i}`} className="bg-[#faf8f2] border border-[#e5e0d8] rounded-sm p-3 flex flex-col gap-2">
            <div className="flex items-start justify-between gap-2">
              <div className="text-[10px] tracking-[0.2em] uppercase text-[#6b655b] font-semibold">Photo {i + 1}</div>
              <div className="flex items-center gap-1">
                <button type="button" onClick={() => move(i, -1)} disabled={i === 0} aria-label="Move up" data-testid={`amen-photo-up-${i}`} className="text-[#6b655b] hover:text-[#234333] disabled:opacity-30 p-1"><ChevronUp size={14} /></button>
                <button type="button" onClick={() => move(i, +1)} disabled={i === list.length - 1} aria-label="Move down" data-testid={`amen-photo-down-${i}`} className="text-[#6b655b] hover:text-[#234333] disabled:opacity-30 p-1"><ChevronDown size={14} /></button>
                <button type="button" onClick={() => remove(i)} aria-label="Remove" data-testid={`amen-photo-remove-${i}`} className="text-red-700 hover:text-red-900 p-1"><X size={14} /></button>
              </div>
            </div>
            <FileUploader
              kind="amenity_photo"
              label=""
              url={p.url}
              onChange={({ url, public_id }) => setAt(i, { url: url || "", public_id: public_id || "" })}
              testid={`amen-photo-file-${i}`}
            />
          </div>
        ))}
        {list.length < A_MAX_PHOTOS && (
          <button type="button" onClick={addSlot} data-testid="amen-photo-add" className="border-2 border-dashed border-[#e5e0d8] hover:border-[#234333] hover:bg-[#f5f1e8] rounded-sm min-h-[160px] flex flex-col items-center justify-center gap-2 text-[#6b655b] hover:text-[#234333] transition-colors">
            <ImageIcon size={22} />
            <span className="text-xs uppercase tracking-widest font-semibold">Add Photo ({list.length}/{A_MAX_PHOTOS})</span>
            <span className="text-[10px] text-[#8a8478]">Up to 200 KB · JPG / PNG / WEBP</span>
          </button>
        )}
      </div>
      {list.length > 0 && (
        <div className="mt-2 text-[11px] text-[#6b655b]">
          {list.length} of {A_MAX_PHOTOS} photos attached · use arrows to reorder
        </div>
      )}
    </div>
  );
}
